import React from "react";
import styled from "@emotion/styled";
import { useNavigate, useLocation, matchPath } from "react-router";
import { RouteID, routePaths } from "../../routing/routes";
import {
  spacing,
  fontSize,
  fontWeight,
  duration,
  border as borderToken,
  interactive,
  text,
} from "@atlas-int/tokens";

export const baseStrokeColor = borderToken.default;
const highlightedColor = interactive.default;

const InactiveLink = styled.a`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  line-height: 1.3;
  box-sizing: border-box;
  font-size: ${fontSize["2xl"]};
  font-weight: ${fontWeight.semibold};
  color: ${text.primary};
  transition: border-color ${duration.fast} ease;
  text-align: center;

  &::after {
    content: "";
    display: block;
    width: 100%;
    height: 0;
    border-bottom: 6px solid transparent;
    margin-top: ${spacing[2]};
    transition: border-color ${duration.fast} ease;
  }

  &:hover {
    border-bottom-color: ${highlightedColor};

    &::after {
      border-bottom-color: ${highlightedColor};
    }
  }
`;

const ActiveLink = styled(InactiveLink)`
  color: ${highlightedColor};
  border-bottom-color: ${highlightedColor};

  &::after {
    border-bottom-color: ${highlightedColor};
  }
`;

const NavItemName = styled.span`
  display: inline-block;
  padding: 0 ${spacing[12]};

  @media (max-width: 1024px) {
    padding-right: 0;
  }
`;

export interface NavLinkDescriptor {
  id: RouteID;
  queryParams?: Record<string, string>;
}

export type NavLinkGroup<K extends string> = Record<K, NavLinkDescriptor>;

interface LinkProps<K extends string> {
  group: NavLinkGroup<K>;
  groupKey: K;
}
type Props<K extends string> = React.PropsWithChildren<LinkProps<K>>;

function generateHref(
  id: RouteID,
  queryParams?: Record<string, string>,
): string {
  const pattern = routePaths[id];
  if (!pattern) return "/";
  // Strip optional path params (e.g. :year?) to get the base path
  const basePath = pattern.replace(/\/:[^/]+\??/g, "");
  const search = queryParams
    ? "?" + new URLSearchParams(queryParams).toString()
    : "";
  return basePath + search;
}

function NavItemLink<K extends string>(props: Props<K>) {
  const { group, groupKey, children } = props;
  const navigate = useNavigate();
  const location = useLocation();

  const descriptor = group[groupKey];
  const href = generateHref(descriptor.id, descriptor.queryParams);

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(href);
  };

  // Determine if this link is active
  const pattern = routePaths[descriptor.id];
  let isActive = false;
  if (pattern) {
    // CountryRankings (/rankings/:year?) should NOT be active when on
    // /rankings/product or /rankings/growth-projections
    if (
      descriptor.id === RouteID.CountryRankings &&
      (location.pathname.startsWith("/rankings/product") ||
        location.pathname.startsWith("/rankings/growth-projections"))
    ) {
      isActive = false;
    } else {
      isActive = !!matchPath({ path: pattern, end: true }, location.pathname);
    }
  }

  const Root = isActive ? ActiveLink : InactiveLink;

  return (
    <Root href={href} onClick={onClick}>
      <NavItemName>{children}</NavItemName>
    </Root>
  );
}

export default NavItemLink;
